import { useMemo, useState } from 'react';

import { useGetCompanyQuestionSessionHistory } from '../../api/apiCompanyQuestion/apiCompanyQuestionQuery';
import { TSicCode } from '../../api/apiCompanyQuestion/fetchPostCompanyQuestion';
import { useGetStateQuery } from '../../api/lendingApi/lendingApiQueries';
import { TGetStateResponse } from '../../types';

export type TQuestionHistory = {
  id: number;
  question: string;
  answer: string;
};

type TSessionData = {
  questions: TQuestionHistory[];
  nextQuestion: string;
};

const readSicCodes = (companyNumber: string): TSicCode => {
  try {
    const data = sessionStorage.getItem(
      `sic_codes_for_company_${companyNumber}`,
    );
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

export function useCompanyQuestionSession() {
  const { state } = useGetStateQuery<TGetStateResponse>();

  const { sessionHistory, isSessionCompleted, isSessionHistoryLoading } =
    useGetCompanyQuestionSessionHistory();

  // sessionHistory is a single, reconciled source of truth (server merged with
  // the per-tab sessionStorage mirror at the query layer), so questions and the
  // next question are both derived from it and can never disagree.
  const sessionData = useMemo<TSessionData>(() => {
    const questions = sessionHistory
      .filter((qa) => qa.answer !== '')
      .map((qa, idx) => ({ id: idx + 1, ...qa }));
    const last = sessionHistory.at(-1);
    const nextQuestion = last && last.answer === '' ? last.question : '';

    return { questions, nextQuestion };
  }, [sessionHistory]);

  const sicCodes = useMemo(() => {
    const companyNumber = state?.application?.company?.company_number;
    return companyNumber ? readSicCodes(companyNumber) : [];
  }, [state?.application?.company?.company_number]);

  const [forceThankYou, setForceThankYou] = useState(false);
  const showThankYou =
    forceThankYou || isSessionCompleted || sessionData.questions.length >= 5;

  return {
    sessionData,
    sicCodes,
    showThankYou,
    forceThankYou: () => setForceThankYou(true),
    isSessionHistoryLoading,
  };
}
